import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Briefcase,
  Search,
  ExternalLink,
  Clock,
  DollarSign,
  MapPin,
  Users,
  Globe,
  ChevronLeft,
  ChevronRight,
  Package,
  Shield,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
  List,
  Tag,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import type { ServiceFull } from './TheOffering.shared';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ServiceStats {
  total_services: number;
  sectors_count: number;
  languages_count: number;
  avg_price_cents: number | null;
  min_duration: number | null;
  max_duration: number | null;
  with_pricing_count: number;
}

interface ServiceListResponse {
  services: ServiceFull[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// ─── Tab definitions ───────────────────────────────────────────────────────────

const SERVICE_TABS = [
  { id: 'itineraries', label: 'Itineraries', icon: List },
  { id: 'inclusions', label: 'Inclusions', icon: Package },
  { id: 'availability', label: 'Availability', icon: Calendar },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'policies', label: 'Policies', icon: Shield },
] as const;

type ServiceTabId = typeof SERVICE_TABS[number]['id'];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(cents: number | null, currency: string = 'EUR'): string {
  if (cents === null || cents === undefined) return '-';
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default: return 'bg-gray-100 text-gray-800';
  }
}

// ─── Loading skeleton ──────────────────────────────────────────────────────────

function ServiceLoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Table skeleton */}
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-10 w-full mb-4" />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────────

function ServiceEmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <Briefcase className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No services found</h3>
        <p className="text-muted-foreground text-center max-w-md">
          This site does not have any services yet. Services are imported
          through the site crawler or can be added via the API.
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Stats summary card ────────────────────────────────────────────────────────

function StatsSummary({ stats, loading }: { stats: ServiceStats | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const items = [
    {
      label: 'Total Services',
      value: stats.total_services?.toString() ?? '0',
      icon: Briefcase,
    },
    {
      label: 'Sectors',
      value: stats.sectors_count?.toString() ?? '0',
      icon: Tag,
    },
    {
      label: 'Avg. Price',
      value: formatCurrency(stats.avg_price_cents),
      icon: DollarSign,
    },
    {
      label: 'Duration Range',
      value: stats.min_duration !== null && stats.max_duration !== null
        ? `${formatDuration(stats.min_duration)} - ${formatDuration(stats.max_duration)}`
        : '-',
      icon: Clock,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <item.icon className="h-4 w-4" />
              {item.label}
            </div>
            <div className="text-2xl font-bold">{item.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Service detail dialog ─────────────────────────────────────────────────────

function ServiceDetailDialog({
  service,
  open,
  onClose,
}: {
  service: ServiceFull | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {service.name}
            <Badge className={getStatusColor(service.status)}>{service.status}</Badge>
          </DialogTitle>
          <DialogDescription>{service.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Language:</span>{' '}
              <span className="font-medium">{service.lang || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Duration:</span>{' '}
              <span className="font-medium">{formatDuration(service.duration_minutes)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Price:</span>{' '}
              <span className="font-medium">
                {service.price_model ? `${service.price_model}: ` : ''}
                {formatCurrency(service.price_from_cents, service.currency)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Group size:</span>{' '}
              <span className="font-medium">
                {service.group_size_min !== null || service.group_size_max !== null
                  ? `${service.group_size_min ?? '?'} - ${service.group_size_max ?? '?'}`
                  : '-'}
              </span>
            </div>
          </div>

          {/* Location */}
          {service.location && (
            <div>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                <MapPin className="h-4 w-4" /> Location
              </h4>
              <p className="text-sm text-muted-foreground">
                {service.location.name && <span>{service.location.name}</span>}
                {service.location.address && (
                  <span>{service.location.name ? ' - ' : ''}{service.location.address}</span>
                )}
              </p>
            </div>
          )}

          {/* Inclusions */}
          {service.inclusions && service.inclusions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" /> Inclusions
              </h4>
              <ul className="text-sm space-y-1">
                {service.inclusions.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-600 mt-0.5">+</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Exclusions */}
          {service.exclusions && service.exclusions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                <XCircle className="h-4 w-4 text-red-600" /> Exclusions
              </h4>
              <ul className="text-sm space-y-1">
                {service.exclusions.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cancellation policy */}
          {service.cancellation_policy && (
            <div>
              <h4 className="text-sm font-semibold mb-1 flex items-center gap-1">
                <Shield className="h-4 w-4" /> Cancellation Policy
              </h4>
              <p className="text-sm text-muted-foreground">{service.cancellation_policy}</p>
            </div>
          )}

          {/* URL */}
          {service.url && (
            <div className="pt-2 border-t">
              <a
                href={service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                View original page
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Service row ───────────────────────────────────────────────────────────────

function ServiceRow({
  service,
  onClick,
}: {
  service: ServiceFull;
  onClick: () => void;
}) {
  return (
    <tr
      className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <td className="p-3">
        <div className="font-medium text-sm truncate max-w-[300px]">{service.name}</div>
        <div className="text-xs text-muted-foreground truncate max-w-[300px]">
          {service.description?.slice(0, 80)}{service.description?.length > 80 ? '...' : ''}
        </div>
      </td>
      <td className="p-3">
        <Badge className={getStatusColor(service.status)} variant="outline">
          {service.status}
        </Badge>
      </td>
      <td className="p-3 text-sm">{service.lang || '-'}</td>
      <td className="p-3 text-sm">{formatDuration(service.duration_minutes)}</td>
      <td className="p-3 text-sm">{formatCurrency(service.price_from_cents, service.currency)}</td>
      <td className="p-3 text-sm">
        {service.location?.name || service.location?.address || '-'}
      </td>
    </tr>
  );
}

// ─── Pagination ────────────────────────────────────────────────────────────────

function Pagination({
  page,
  pages,
  total,
  onPageChange,
}: {
  page: number;
  pages: number;
  total: number;
  onPageChange: (p: number) => void;
}) {
  if (pages <= 1) return null;

  return (
    <div className="flex items-center justify-between pt-4">
      <span className="text-sm text-muted-foreground">
        {total.toLocaleString()} service{total !== 1 ? 's' : ''} total
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm">
          Page {page} of {pages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Tab: Itineraries ──────────────────────────────────────────────────────────

function ItinerariesTab({
  services,
  loading,
  page,
  pages,
  total,
  onPageChange,
  onSelect,
}: {
  services: ServiceFull[];
  loading: boolean;
  page: number;
  pages: number;
  total: number;
  onPageChange: (p: number) => void;
  onSelect: (s: ServiceFull) => void;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (services.length === 0) {
    return <ServiceEmptyState />;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground">
              <th className="p-3 font-medium">Service</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Lang</th>
              <th className="p-3 font-medium">Duration</th>
              <th className="p-3 font-medium">Price From</th>
              <th className="p-3 font-medium">Location</th>
            </tr>
          </thead>
          <tbody>
            {services.map((svc) => (
              <ServiceRow key={svc.id} service={svc} onClick={() => onSelect(svc)} />
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={pages} total={total} onPageChange={onPageChange} />
    </div>
  );
}

// ─── Tab: Inclusions ───────────────────────────────────────────────────────────

function InclusionsTab({
  services,
  loading,
}: {
  services: ServiceFull[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const withInclusions = services.filter(
    (s) => (s.inclusions && s.inclusions.length > 0) || (s.exclusions && s.exclusions.length > 0)
  );

  if (withInclusions.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No services on this page have inclusions or exclusions defined.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {withInclusions.map((svc) => (
        <Card key={svc.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{svc.name}</CardTitle>
            <CardDescription className="text-xs">{svc.slug}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {svc.inclusions && svc.inclusions.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">
                    Inclusions ({svc.inclusions.length})
                  </h5>
                  <ul className="text-sm space-y-0.5">
                    {svc.inclusions.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {svc.exclusions && svc.exclusions.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                    Exclusions ({svc.exclusions.length})
                  </h5>
                  <ul className="text-sm space-y-0.5">
                    {svc.exclusions.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Tab: Availability ─────────────────────────────────────────────────────────

function AvailabilityTab({
  services,
  loading,
}: {
  services: ServiceFull[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const withAvailability = services.filter(
    (s) =>
      s.availability_window !== null ||
      s.booking_lead_days !== null ||
      s.group_size_min !== null ||
      s.group_size_max !== null ||
      (s.languages && s.languages.length > 0)
  );

  if (withAvailability.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No availability data found for services on this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="p-3 font-medium">Service</th>
            <th className="p-3 font-medium">Duration</th>
            <th className="p-3 font-medium">Group Size</th>
            <th className="p-3 font-medium">Languages</th>
            <th className="p-3 font-medium">Booking Lead</th>
          </tr>
        </thead>
        <tbody>
          {withAvailability.map((svc) => (
            <tr key={svc.id} className="border-b">
              <td className="p-3">
                <div className="font-medium text-sm">{svc.name}</div>
              </td>
              <td className="p-3 text-sm">{formatDuration(svc.duration_minutes)}</td>
              <td className="p-3 text-sm">
                {svc.group_size_min !== null || svc.group_size_max !== null
                  ? `${svc.group_size_min ?? '?'} - ${svc.group_size_max ?? '?'}`
                  : '-'}
              </td>
              <td className="p-3 text-sm">
                {svc.languages && svc.languages.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {svc.languages.map((lang) => (
                      <Badge key={lang} variant="outline" className="text-xs">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </td>
              <td className="p-3 text-sm">
                {svc.booking_lead_days !== null ? `${svc.booking_lead_days} days` : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab: Pricing ──────────────────────────────────────────────────────────────

function PricingTab({
  services,
  loading,
}: {
  services: ServiceFull[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const withPricing = services.filter(
    (s) => s.price_from_cents !== null || s.price_model !== null
  );

  if (withPricing.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <DollarSign className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No pricing data found for services on this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="p-3 font-medium">Service</th>
            <th className="p-3 font-medium">Price Model</th>
            <th className="p-3 font-medium">Price From</th>
            <th className="p-3 font-medium">Currency</th>
          </tr>
        </thead>
        <tbody>
          {withPricing.map((svc) => (
            <tr key={svc.id} className="border-b">
              <td className="p-3">
                <div className="font-medium text-sm">{svc.name}</div>
              </td>
              <td className="p-3 text-sm">
                {svc.price_model ? (
                  <Badge variant="outline">{svc.price_model}</Badge>
                ) : (
                  '-'
                )}
              </td>
              <td className="p-3 text-sm font-medium">
                {formatCurrency(svc.price_from_cents, svc.currency)}
              </td>
              <td className="p-3 text-sm">{svc.currency || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab: Policies ─────────────────────────────────────────────────────────────

function PoliciesTab({
  services,
  loading,
}: {
  services: ServiceFull[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const withPolicies = services.filter(
    (s) => s.cancellation_policy !== null
  );

  if (withPolicies.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Shield className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No cancellation policies found for services on this page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {withPolicies.map((svc) => (
        <Card key={svc.id}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-sm font-medium">{svc.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{svc.cancellation_policy}</p>
              </div>
              {svc.booking_lead_days !== null && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="shrink-0">
                        {svc.booking_lead_days}d lead
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      Booking lead time: {svc.booking_lead_days} days
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function ServiceOffering() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id;

  // Deep link: read initial tab from URL
  const [activeTab, setActiveTab] = useState<ServiceTabId>(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && SERVICE_TABS.some((t) => t.id === tab)) {
      return tab as ServiceTabId;
    }
    return 'itineraries';
  });

  // Data state
  const [services, setServices] = useState<ServiceFull[]>([]);
  const [stats, setStats] = useState<ServiceStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [langFilter, setLangFilter] = useState<string>('all');

  const LIMIT = 50;

  // Update URL when tab changes
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab as ServiceTabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Fetch stats
  useEffect(() => {
    if (!siteId) return;
    setStatsLoading(true);
    authFetch(`/api/services/stats/${siteId}`)
      .then((res) => res.json())
      .then((data) => {
        setStats(data);
        setStatsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch service stats:', err);
        setStatsLoading(false);
      });
  }, [siteId]);

  // Fetch services list
  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      page: page.toString(),
      limit: LIMIT.toString(),
    });
    if (search) params.set('search', search);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (langFilter !== 'all') params.set('lang', langFilter);

    authFetch(`/api/services/${siteId}?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: ServiceListResponse) => {
        setServices(data.services);
        setTotal(data.total);
        setPages(data.pages);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch services:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [siteId, page, search, statusFilter, langFilter]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Detail dialog
  const [selectedService, setSelectedService] = useState<ServiceFull | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleSelectService = useCallback((svc: ServiceFull) => {
    setSelectedService(svc);
    setDetailOpen(true);
  }, []);

  // No site selected
  if (!siteId) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Info className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Select a site to view its services.</p>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !loading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <XCircle className="h-12 w-12 text-red-500 mb-3" />
          <h3 className="text-lg font-semibold mb-2">Failed to load services</h3>
          <p className="text-muted-foreground text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats summary */}
      <StatsSummary stats={stats} loading={statsLoading} />

      {/* Filters bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search services..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={langFilter} onValueChange={(v) => { setLangFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All langs</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
                <SelectItem value="it">Italian</SelectItem>
                <SelectItem value="pt">Portuguese</SelectItem>
                <SelectItem value="ca">Catalan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="border-b px-4 pt-4">
              <TabsList>
                {SERVICE_TABS.map((tab) => (
                  <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1.5">
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="p-4">
              <TabsContent value="itineraries" className="mt-0">
                <ItinerariesTab
                  services={services}
                  loading={loading}
                  page={page}
                  pages={pages}
                  total={total}
                  onPageChange={setPage}
                  onSelect={handleSelectService}
                />
              </TabsContent>

              <TabsContent value="inclusions" className="mt-0">
                <InclusionsTab services={services} loading={loading} />
              </TabsContent>

              <TabsContent value="availability" className="mt-0">
                <AvailabilityTab services={services} loading={loading} />
              </TabsContent>

              <TabsContent value="pricing" className="mt-0">
                <PricingTab services={services} loading={loading} />
              </TabsContent>

              <TabsContent value="policies" className="mt-0">
                <PoliciesTab services={services} loading={loading} />
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <ServiceDetailDialog
        service={selectedService}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}
